import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { switchMap, map } from 'rxjs';
import { get } from 'lodash-es';
import { normalizeAcceptedTypes, validateUploadFiles } from '../upload/upload-validation.util';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { UserStore } from 'app/core/services/user.store';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { UtilityService } from '../../services/utility.service';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { MatUploadButtonComponent } from '../mat-upload-button.component';
import { debugLog } from '../../utils/debug-logger';
import type { Taxonomy } from '../../../features/admin/modules/modules.model';
import type {
  SigningDocumentEntry,
  SigningDocumentMetadata,
  SigningEntry,
  SigningEntryRef,
  SigningSubmitter,
  SigningTaxonomyValue,
  SigningUploadState,
} from './signing.types';
import { SigningStatusEnum } from './signing.types';
import { commitUserValue } from './user-edit.util';

/**
 * Formly field 'signing-upload' — HR uploads a .docx that must be signed.
 *
 * Two-step create:
 * 1. Document entry in the documents module (category via `props.documentType`,
 *    share 'private', file uploaded as private document).
 * 2. Signing entry in the signing module with `document` = the document entry,
 *    `submitter` = the user resolved from `props.submitterFieldPath` in the
 *    current form model, and status 'nieuw' — which triggers the backend
 *    `signDocuments` action (DocuSeal) on entryCreated.
 *
 * Field value after success: a SigningEntryRef, so a module action can copy it
 * to the onboarding entry.
 *
 * While not yet signed the request can be removed: this deletes the signing
 * entry (and its document entry), clears the value and re-enables upload.
 */
@Component({
  selector: 'formly-field-signing-upload',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatUploadButtonComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    MatProgressSpinner,
    FormlyErrorComponent,
    FormlyDataCyDirective,
  ],
  template: `
    <div class="flex flex-col mb-4" [formlyDataCy]="field">
      @if (props.label) {
        <span class="font-medium inline-block mb-2"> {{ props.label }}@if (props.required) {<span class="dd-required-star"> *</span>} </span>
      }

      @if (!isDisabled() && !hasEntry() && !uploadState().isUploading) {
        <mat-file-upload
          class="flex-1"
          [info]="props.info"
          [descriptionText]="props.description"
          [selectButtonText]="props.selectButtonText || 'Kies document'"
          [allowMultipleFiles]="false"
          [showUploadButton]="false"
          [acceptedTypes]="acceptAttribute()"
          (selectedFilesChanged)="onFileSelected($event)">
        </mat-file-upload>
      }

      @if (uploadState().isUploading) {
        <div class="flex items-center gap-2 p-4 border rounded-lg bg-light-tint">
          <mat-spinner diameter="24"></mat-spinner>
          <span class="text-sm">Document wordt aangemaakt en verstuurd ter ondertekening…</span>
        </div>
      }

      @if (uploadState().error) {
        <div class="p-4 border rounded-lg bg-red-50 dark:bg-red-900/20">
          <div class="flex items-center gap-2 text-red-600 dark:text-red-400">
            <mat-icon class="icon-size-4">error</mat-icon>
            <span class="text-sm">{{ uploadState().error }}</span>
          </div>
        </div>
      }

      @if (hasEntry()) {
        <div class="fuse-card bg-light-tint my-3 p-4 border">
          <div class="flex items-center justify-between w-full gap-4">
            <div class="flex items-center gap-4 min-w-0">
              <mat-icon class="text-hint">description</mat-icon>
              <div class="flex flex-col min-w-0">
                <span class="text-md font-medium truncate">{{ entryName() }}</span>
                <span class="text-sm text-accent">{{
                  isSigned() ? 'Ondertekend' : 'Ter ondertekening aangeboden'
                }}</span>
              </div>
            </div>

            <div class="flex items-center gap-1 shrink-0">
              @if (canDownload()) {
                <button
                  mat-icon-button
                  type="button"
                  (click)="downloadDocument()"
                  matTooltip="Ondertekend document downloaden">
                  <mat-icon svgIcon="feather:download"></mat-icon>
                </button>
              }

              @if (canRemove()) {
                <button
                  mat-icon-button
                  type="button"
                  (click)="remove()"
                  [disabled]="removing()"
                  matTooltip="Verwijderen en opnieuw uploaden">
                  <mat-icon svgIcon="feather:trash-2"></mat-icon>
                </button>
              }
            </div>
          </div>
        </div>
      }

      @if (props.description && !uploadState().isUploading) {
        <div class="mt-2 text-sm text-accent">{{ props.description }}</div>
      }

      @if (field.formControl.errors) {
        <app-formly-error [field]="field"></app-formly-error>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldSigningUploadComponent extends FieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #utilityService = inject(UtilityService);
  readonly #userStore = inject(UserStore);
  readonly #confirmationService = inject(FuseConfirmationService);

  readonly uploadState = signal<SigningUploadState>({ isUploading: false, error: null });
  readonly removing = signal(false);
  readonly #entrySignal = signal<SigningEntry | null>(null);

  readonly hasEntry = computed(() => this.#entrySignal() !== null);
  readonly entryName = computed(
    () => this.#entrySignal()?.entry?.name ?? this.field.formControl.value?.selectedInput ?? 'Document'
  );
  /** Status slug, lowercase-normalized (webhook writes 'Verlopen' capitalized). */
  readonly #statusSlug = computed(() => this.#entrySignal()?.entry?.status?.slug?.toLowerCase() ?? null);
  readonly isSigned = computed(() => this.#statusSlug() === SigningStatusEnum.Ondertekend);
  readonly canRemove = computed(() => !this.isDisabled() && this.hasEntry() && !this.isSigned());

  readonly #documentEntry = computed(() => this.#entrySignal()?.entry?.document ?? null);
  readonly documentMetadata = computed<SigningDocumentMetadata | null>(() => {
    const documentEntry = this.#documentEntry();
    if (!documentEntry) return null;
    // Prefer the candidate that actually carries a documentId (the private/public
    // arrays can be present but empty/partial; the resolved id lives in entry.file).
    const candidates = [
      documentEntry.privateDocuments?.[0],
      documentEntry.publicDocuments?.[0],
      documentEntry.entry?.file,
    ];
    return candidates.find(meta => meta?.documentId) ?? candidates.find(Boolean) ?? null;
  });
  /** Signed document can be downloaded once it exists. */
  readonly canDownload = computed(() => this.isSigned() && !!this.documentMetadata());

  /**
   * Accepted types from the field config. Falls back to .docx — the single type this field had
   * hardcoded before the setting became reachable for it (#20817) — so existing forms keep working.
   */
  readonly #acceptedTypes = computed(() => {
    const types = normalizeAcceptedTypes(this.props?.['acceptedTypes']);
    return types.length ? types : ['.docx'];
  });

  readonly acceptAttribute = computed(() => this.#acceptedTypes().join(','));

  readonly #signingModuleId = computed(() => this.#modulesStore.moduleCodeToId('signing'));
  readonly #documentsModuleId = computed(() => this.#modulesStore.moduleCodeToId('documents'));

  ngOnInit(): void {
    // Re-opened form: the value is a SigningEntryRef (or a full entry). Fetch the
    // live signing entry so name + status are accurate (status gates cancel).
    const currentValue = this.field.formControl.value;
    if (currentValue && typeof currentValue === 'object' && currentValue.id) {
      if (currentValue.entry) {
        this.#entrySignal.set(currentValue);
      } else {
        this.#fetchEntry(currentValue.id);
      }
    }
    this.#updateRequiredValidity();
  }

  isDisabled(): boolean {
    return this.props.disabled || this.form.disabled;
  }

  onFileSelected(files: FileList): void {
    if (!files || files.length === 0) return;

    this.field.formControl.markAsTouched();
    this.uploadState.set({ isUploading: false, error: null });

    const file = files[0];
    // One source for what is allowed: the same list that feeds the picker's accept attribute, run
    // through the shared validator. Hardcoding .docx here would contradict a configured type and
    // leave the field rejecting every file the picker just offered.
    const validation = validateUploadFiles(files, this.#acceptedTypes(), this.props?.['maxFileSizeMb'] as number);
    if (validation.error) {
      this.uploadState.set({ isUploading: false, error: validation.error });
      return;
    }

    if (!this.#signingModuleId() || !this.#documentsModuleId()) {
      debugLog('signing-upload', 'signing or documents module not resolvable');
      this.uploadState.set({ isUploading: false, error: 'De benodigde modules zijn niet beschikbaar.' });
      return;
    }

    const submitter = this.#resolveSubmitter();
    if (!submitter) {
      this.uploadState.set({ isUploading: false, error: 'Selecteer eerst een gebruiker als ondertekenaar.' });
      return;
    }

    const statusNieuw = this.#resolveStatus(SigningStatusEnum.Nieuw);
    if (!statusNieuw) {
      debugLog('signing-upload', `status taxonomy value '${SigningStatusEnum.Nieuw}' not found on signing module`);
      this.uploadState.set({ isUploading: false, error: `Statuswaarde '${SigningStatusEnum.Nieuw}' niet gevonden.` });
      return;
    }

    this.#createEntries(files, file.name, submitter, statusNieuw);
  }

  /** Download the (signed) document via the documents-module entry. */
  downloadDocument(): void {
    const documentEntry = this.#documentEntry();
    const metadata = this.documentMetadata();
    if (!documentEntry || !metadata) return;

    this.#modulesStore.downloadDocument({
      moduleId: this.#documentsModuleId(),
      entryId: documentEntry.id,
      documentId: metadata.documentId,
      documentName: metadata.documentName,
      blobUrl: metadata.blobUrl,
    });
  }

  /** Delete the signing entry (+ its document entry) and re-enable upload. */
  remove(): void {
    const entry = this.#entrySignal();
    const moduleId = this.#signingModuleId();
    if (!entry?.id || !moduleId) return;

    this.#confirmationService
      .open({
        title: 'Ondertekenverzoek verwijderen',
        message: 'Het ondertekenverzoek en het document worden verwijderd. Je kunt daarna een nieuw document uploaden.',
        actions: { cancel: { label: 'Terug' }, confirm: { label: 'Verwijderen' } },
      })
      .afterClosed()
      .subscribe(result => {
        if (result === 'confirmed') this.#deleteEntry(entry);
      });
  }

  /**
   * Step 1: document entry (private) in the documents module.
   * Step 2: signing entry referencing the document entry — single POST so the
   * backend `signDocuments` action can fire on entryCreated with everything set.
   */
  #createEntries(files: FileList, fileName: string, submitter: SigningSubmitter, status: SigningTaxonomyValue): void {
    this.uploadState.set({ isUploading: true, error: null });

    const documentData: SigningDocumentEntry['entry'] = {
      archived: false,
      name: fileName,
      user: this.#userStore.currentUserEntry(),
      company: this.#userStore.currentCompanyEntry(),
      location: this.#userStore.currentLocationEntry(),
      'document-type': this.props.documentType || null,
      share: 'private',
      file: { documentName: fileName },
    } as SigningDocumentEntry['entry'];

    this.#entriesService
      .addEntry(this.#documentsModuleId(), documentData, {
        fieldKey: 'entry.file',
        files,
        isPublic: false,
      })
      .pipe(
        switchMap((documentEntry: SigningDocumentEntry) => {
          debugLog('signing-upload', 'document entry created', documentEntry.id);
          const signingData: SigningEntry['entry'] = {
            archived: false,
            name: fileName,
            document: documentEntry,
            submitter,
            status,
          };
          return this.#entriesService
            .addEntry(this.#signingModuleId(), signingData)
            .pipe(map((signingEntry: SigningEntry) => ({ signingEntry, documentEntry })));
        })
      )
      .subscribe({
        next: ({ signingEntry }) => {
          debugLog('signing-upload', 'signing entry created', signingEntry.id);
          this.#entrySignal.set(signingEntry);
          commitUserValue(this.field.formControl, {
            id: signingEntry.id,
            selectedInput: signingEntry.selectedInput ?? signingEntry.entry?.name ?? fileName,
          } satisfies SigningEntryRef);
          this.#updateRequiredValidity();
          this.uploadState.set({ isUploading: false, error: null });
          this.#utilityService.notify('Document aangemaakt en verstuurd ter ondertekening.');
        },
        error: error => {
          console.error('signing-upload: create failed', error);
          this.uploadState.set({
            isUploading: false,
            error: 'Er is een fout opgetreden bij het aanmaken van het ondertekenverzoek.',
          });
        },
      });
  }

  #deleteEntry(entry: SigningEntry): void {
    this.removing.set(true);
    const documentEntryId = entry.entry?.document?.id;

    this.#entriesService.delete(this.#signingModuleId(), entry.id).subscribe({
      next: () => {
        // Best-effort cleanup of the orphaned document entry.
        if (documentEntryId && this.#documentsModuleId()) {
          this.#entriesService.delete(this.#documentsModuleId(), documentEntryId).subscribe({
            error: err => debugLog('signing-upload', 'document entry delete failed', err),
          });
        }
        this.#resetField();
        this.#utilityService.notify('Ondertekenverzoek verwijderd.');
      },
      error: error => {
        // Treat already-removed as success so the user can re-upload.
        if (error?.status === 400 || error?.status === 404) {
          this.#resetField();
          this.#utilityService.notify('Ondertekenverzoek verwijderd.');
          return;
        }
        debugLog('signing-upload', 'signing entry delete failed', error);
        this.removing.set(false);
        this.#utilityService.notify('Verwijderen is niet gelukt.');
      },
    });
  }

  #resetField(): void {
    this.#entrySignal.set(null);
    // Only reached after the user removed the signing request, so the entry control is edited.
    commitUserValue(this.field.formControl, null);
    this.uploadState.set({ isUploading: false, error: null });
    this.removing.set(false);
    this.#updateRequiredValidity();
  }

  #fetchEntry(entryId: string): void {
    const moduleId = this.#signingModuleId();
    if (!moduleId) return;
    this.#entriesService.getEntry(moduleId, entryId).subscribe({
      next: (entry: SigningEntry) => this.#entrySignal.set(entry),
      error: err => debugLog('signing-upload', `failed to fetch signing entry ${entryId}`, err),
    });
  }

  /**
   * Resolve the (single) submitter from the current form model via
   * `props.submitterFieldPath`, as an entrySmall `{ id, selectedInput }`.
   * Accepts a full entry, an entrySmall or a tag-shaped `{entryId,label}` value;
   * uses the first element for arrays.
   */
  #resolveSubmitter(): SigningSubmitter | null {
    const path = this.props.submitterFieldPath as string | undefined;
    if (!path) {
      debugLog('signing-upload', 'props.submitterFieldPath not configured');
      return null;
    }

    const normalizedPath = path.startsWith('entry.') ? path.substring(6) : path;
    let value = get(this.model, normalizedPath) ?? get(this.options?.formState?.entry?.entry ?? {}, normalizedPath);
    if (Array.isArray(value)) value = value[0];
    if (!value || typeof value !== 'object') return null;

    if (value.id) {
      return { id: value.id, selectedInput: value.selectedInput ?? value.entry?.name ?? value.name };
    }
    if (value.entryId) {
      return { id: value.entryId, selectedInput: value.label };
    }
    return null;
  }

  /** Find a status taxonomy value (by slug) on the signing module. */
  #resolveStatus(slug: string): SigningTaxonomyValue | null {
    const taxonomies = this.#modulesStore.taxonomies()[this.#signingModuleId()];
    const statusTaxonomy = taxonomies?.find((taxonomy: Taxonomy) => taxonomy.name?.toLowerCase() === 'status');
    return statusTaxonomy?.entries?.find(entry => entry.slug === slug) ?? null;
  }

  #updateRequiredValidity(): void {
    if (!this.props.required) return;
    const value = this.field.formControl.value;
    this.field.formControl.setErrors(value?.id ? null : { required: true });
  }
}
